// Consent-based usage statistics (TTDSG § 25 / Art. 6 Abs. 1 lit. a DSGVO).
//
// Without the visitor's "Statistik erlauben" nothing is stored in the browser and nothing is
// sent. With consent: a random visitor id in localStorage, one session per visit (ends after
// 30 minutes of inactivity), page_view on route change, heartbeat every 15 s while the tab is
// visible, page_leave with the visible time. Paths are sent without query string; tenant
// tokens and ids are masked (again on the server). No IP address is stored.
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { configured, supabase } from "./supabase";

export type ConsentLevel = "necessary" | "statistics";
export const CONSENT_KEY = "kk_consent_v1";
/** Bumped when the banner text changes what a "yes" covers (2: sessions linked to the account). */
export const CONSENT_VERSION = 2;
export const VISITOR_KEY = "kk_stat_vid";
export const SESSION_KEY = "kk_stat_sid";
export const CONSENT_EVENT = "kk-consent-change";
export const HEARTBEAT_MS = 15_000;
const SESSION_IDLE_MS = 25 * 60_000; // below the server's 30 min

/** Minimal storage interface (localStorage/sessionStorage or a test double). */
export interface KV { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void }

function safe(get: () => Storage): KV | null {
  try { const s = get(); return s ?? null; } catch { return null; }
}
export const localKV = (): KV | null => (typeof window === "undefined" ? null : safe(() => window.localStorage));
export const sessionKV = (): KV | null => (typeof window === "undefined" ? null : safe(() => window.sessionStorage));

// Consent ------------------------------------------------------------------------------
export function readConsent(kv: KV | null = localKV()): ConsentLevel | null {
  try {
    const raw = kv?.getItem(CONSENT_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { level?: unknown; v?: unknown };
    // a "statistics" consent given to an older banner text (v1 said "anonym") is asked again
    if (v.level === "statistics") return v.v === CONSENT_VERSION ? "statistics" : null;
    return v.level === "necessary" ? "necessary" : null;
  } catch { return null; }
}

export function writeConsent(level: ConsentLevel, kv: KV | null = localKV(), skv: KV | null = sessionKV()): void {
  try { kv?.setItem(CONSENT_KEY, JSON.stringify({ level, at: new Date().toISOString(), v: CONSENT_VERSION })); } catch { /* storage blocked */ }
  if (level !== "statistics") clearAnalyticsIds(kv, skv);
  else if (refMem) { try { kv?.setItem(REF_KEY, refMem); } catch { /* blocked */ } }
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: level }));
}

export function clearAnalyticsIds(kv: KV | null = localKV(), skv: KV | null = sessionKV()): void {
  try { kv?.removeItem(VISITOR_KEY); } catch { /* ignore */ }
  try { kv?.removeItem(REF_KEY); } catch { /* ignore */ }
  try { skv?.removeItem(SESSION_KEY); } catch { /* ignore */ }
}

export const statisticsAllowed = (kv: KV | null = localKV()) => readConsent(kv) === "statistics";

// Data minimisation --------------------------------------------------------------------
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** Path without query/hash; tenant token and ids masked. */
export function normalizePath(path: string): string {
  let p = (path || "/").split("#")[0]!.split("?")[0]!;
  if (!p.startsWith("/")) p = "/" + p;
  p = p.replace(/^\/m\/[^/]+/, "/m/:token").replace(UUID, ":id");
  return p.slice(0, 300);
}

/** Only the host name of an external referrer; own host and invalid values give null. */
export function referrerDomain(referrer: string, ownHost: string): string | null {
  if (!referrer) return null;
  try {
    const h = new URL(referrer).hostname.toLowerCase();
    if (!h || h === ownHost.toLowerCase() || !/^[a-z0-9.-]{1,253}$/.test(h)) return null;
    return h;
  } catch { return null; }
}

/** Coarse device class ("Chrome · macOS · Desktop"); never the full user-agent string. */
export function shortUserAgent(ua: string): string {
  const browser = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Firefox\//.test(ua) ? "Firefox"
    : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "Andere";
  const os = /iPhone|iPad|iPod/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "macOS"
    : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "Andere";
  const device = /Mobi|iPhone|Android.*Mobile/.test(ua) ? "Mobil" : /iPad|Tablet|Android/.test(ua) ? "Tablet" : "Desktop";
  return `${browser} · ${os} · ${device}`;
}

/** Admin pages are never tracked. */
export const isTrackedPath = (path: string) => !/^\/admin(\/|$)/.test(path);

function uuid(): string {
  const c: Crypto = globalThis.crypto;
  if (typeof c.randomUUID === "function") return c.randomUUID();
  const b = c.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40; b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// Tracker ------------------------------------------------------------------------------
export interface StartArgs { visitorId: string; path: string; referrerDomain: string | null; userAgent: string }
export interface EventArgs { sessionId: string; visitorId: string; type: "page_view" | "heartbeat" | "page_leave" | "event"; path: string; durationMs?: number; name?: string; props?: Record<string, unknown> }
export interface Transport {
  start(a: StartArgs): Promise<string | null>;
  event(a: EventArgs): Promise<boolean>;
  /** Fire-and-forget that survives page unload (fetch keepalive). */
  beacon(a: EventArgs): void;
}

export interface TrackerOptions {
  transport: Transport;
  local?: KV | null;
  session?: KV | null;
  now?: () => number;
  referrer?: () => string | null;
  userAgent?: () => string;
}

interface Page { path: string; visibleSince: number | null; accumulated: number }

export class Tracker {
  private t: Transport; private local: KV | null; private ses: KV | null; private now: () => number;
  private ref: () => string | null; private ua: () => string;
  private sessionId: string | null = null; private lastActivity = 0; private page: Page | null = null;
  private queue: Promise<unknown> = Promise.resolve();

  constructor(o: TrackerOptions) {
    this.t = o.transport; this.local = o.local === undefined ? localKV() : o.local;
    this.ses = o.session === undefined ? sessionKV() : o.session;
    this.now = o.now ?? Date.now; this.ref = o.referrer ?? (() => null); this.ua = o.userAgent ?? (() => "");
  }

  get enabled() { return readConsent(this.local) === "statistics"; }
  /** Resolves when all queued calls have been sent (for tests). */
  flush() { return this.queue; }

  private visitorId(): string | null {
    if (!this.enabled) return null;
    try {
      let v = this.local?.getItem(VISITOR_KEY) ?? null;
      if (!v || !/^[0-9a-f-]{36}$/i.test(v)) { v = uuid(); this.local?.setItem(VISITOR_KEY, v); }
      return v;
    } catch { return null; }
  }

  private restoreSession() {
    if (this.sessionId) return;
    try {
      const raw = this.ses?.getItem(SESSION_KEY);
      if (!raw) return;
      const s = JSON.parse(raw) as { id: string; last: number };
      if (typeof s.id === "string" && this.now() - s.last < SESSION_IDLE_MS) { this.sessionId = s.id; this.lastActivity = s.last; }
    } catch { /* ignore */ }
  }

  private touch() {
    this.lastActivity = this.now();
    try { if (this.sessionId) this.ses?.setItem(SESSION_KEY, JSON.stringify({ id: this.sessionId, last: this.lastActivity })); } catch { /* ignore */ }
  }

  private async ensureSession(path: string, vid: string, fresh = false): Promise<string | null> {
    this.restoreSession();
    if (fresh || (this.sessionId && this.now() - this.lastActivity >= SESSION_IDLE_MS)) this.sessionId = null;
    if (this.sessionId) return this.sessionId;
    const id = await this.t.start({ visitorId: vid, path: normalizePath(path), referrerDomain: this.ref(), userAgent: this.ua() });
    if (!id) return null;
    this.sessionId = id; this.touch();
    return id;
  }

  private enqueue(fn: () => Promise<unknown>) {
    this.queue = this.queue.then(fn).catch(() => undefined);
    return this.queue;
  }

  private send(type: EventArgs["type"], path: string, extra: Partial<EventArgs> = {}) {
    return this.enqueue(async () => {
      if (!this.enabled) return;
      const vid = this.visitorId(); if (!vid) return;
      let sid = await this.ensureSession(path, vid); if (!sid) return;
      const args = { sessionId: sid, visitorId: vid, type, path: normalizePath(path), ...extra };
      let ok = await this.t.event(args);
      if (!ok && type !== "heartbeat") { // session expired or unknown: start a new one, retry once
        sid = await this.ensureSession(path, vid, true); if (!sid) return;
        ok = await this.t.event({ ...args, sessionId: sid });
      }
      if (ok) this.touch();
    });
  }

  private visibleMs(p: Page) { return p.accumulated + (p.visibleSince === null ? 0 : this.now() - p.visibleSince); }

  /** Route change: closes the previous page (page_leave) and records a page_view. */
  pageView(path: string, visible = true) {
    if (!this.enabled || !isTrackedPath(path)) { this.leave(false); return Promise.resolve(); }
    if (this.page && normalizePath(this.page.path) === normalizePath(path)) return this.queue;
    this.leave(false);
    this.page = { path, visibleSince: visible ? this.now() : null, accumulated: 0 };
    return this.send("page_view", path);
  }

  heartbeat() {
    if (!this.enabled || !this.page || this.page.visibleSince === null) return Promise.resolve();
    return this.send("heartbeat", this.page.path);
  }

  /** Records the visible time of the current page. beacon=true for unload/hidden. */
  leave(beacon: boolean) {
    const p = this.page;
    if (!p) return;
    if (!beacon) this.page = null;
    const ms = Math.min(Math.max(Math.round(this.visibleMs(p)), 0), 14_400_000);
    p.accumulated = 0; p.visibleSince = null;
    if (!this.enabled || ms <= 0) return;
    if (beacon) {
      this.restoreSession();
      const vid = this.visitorId();
      if (this.sessionId && vid) {
        this.t.beacon({ sessionId: this.sessionId, visitorId: vid, type: "page_leave", path: normalizePath(p.path), durationMs: ms });
        this.touch();
      }
    } else {
      void this.send("page_leave", p.path, { durationMs: ms });
    }
  }

  /** Tab hidden: send what we have; tab visible again: keep counting on the same page. */
  hidden() { this.leave(true); }
  visible() { if (this.page && this.page.visibleSince === null) this.page.visibleSince = this.now(); }

  /** Custom event (e.g. "upload_started"); props must be small and contain no personal data. */
  event(name: string, props?: Record<string, unknown>) {
    if (!this.enabled) return Promise.resolve();
    return this.send("event", this.page?.path ?? (typeof location !== "undefined" ? location.pathname : "/"), { name, props });
  }

  /** Consent withdrawn: forget everything locally. */
  reset() {
    this.page = null; this.sessionId = null;
    clearAnalyticsIds(this.local, this.ses);
  }
}

// Supabase transport -------------------------------------------------------------------
const URL_ = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
let accessToken: string | null = null;

const rpcBody = (a: EventArgs) => ({
  p_session_id: a.sessionId, p_visitor_id: a.visitorId, p_type: a.type, p_path: a.path,
  p_duration_ms: a.durationMs ?? null, p_name: a.name ?? null, p_props: a.props ?? null,
});

export const supabaseTransport: Transport = {
  async start(a) {
    const { data, error } = await supabase.rpc("track_start", {
      p_visitor_id: a.visitorId, p_path: a.path, p_referrer_domain: a.referrerDomain, p_user_agent: a.userAgent, p_consent: "statistics",
    });
    return error ? null : ((data as string | null) ?? null);
  },
  async event(a) {
    const { data, error } = await supabase.rpc("track_event", rpcBody(a));
    return !error && data === true;
  },
  beacon(a) {
    if (!URL_ || !ANON) return;
    try {
      void fetch(`${URL_}/rest/v1/rpc/track_event`, {
        method: "POST", keepalive: true,
        headers: { apikey: ANON, Authorization: `Bearer ${accessToken ?? ANON}`, "Content-Type": "application/json" },
        body: JSON.stringify(rpcBody(a)),
      }).catch(() => undefined);
    } catch { /* ignore */ }
  },
};

let shared: Tracker | null = null;
/** The app-wide tracker (null if Supabase is not configured). */
export function getTracker(): Tracker | null {
  if (!configured || typeof window === "undefined") return null;
  if (!shared) {
    shared = new Tracker({
      transport: supabaseTransport,
      referrer: () => referrerDomain(document.referrer, location.hostname),
      userAgent: () => shortUserAgent(navigator.userAgent),
    });
    supabase.auth.getSession().then(({ data }) => { accessToken = data.session?.access_token ?? null; }).catch(() => undefined);
    supabase.auth.onAuthStateChange((_e, s) => { accessToken = s?.access_token ?? null; });
  }
  return shared;
}

/** Records a custom event if (and only if) the visitor consented. */
export function trackEvent(name: string, props?: Record<string, unknown>) { void getTracker()?.event(name, props); }

/** Hook: page views on route change, heartbeat, page_leave on hide/unload. Mount once inside the router. */
export function useAnalyticsTracking(consent: ConsentLevel | null) {
  const location = useLocation();
  const tracker = useRef<Tracker | null>(null);
  if (tracker.current === null) tracker.current = getTracker();

  useEffect(() => {
    const t = tracker.current;
    if (!t) return;
    if (consent !== "statistics") { t.reset(); return; }
    void t.pageView(location.pathname, document.visibilityState === "visible");
  }, [location.pathname, consent]);

  useEffect(() => {
    const t = tracker.current;
    if (!t || consent !== "statistics") return;
    const iv = window.setInterval(() => { if (document.visibilityState === "visible") void t.heartbeat(); }, HEARTBEAT_MS);
    const onVis = () => (document.visibilityState === "hidden" ? t.hidden() : t.visible());
    const onHide = () => t.hidden();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onHide);
    return () => {
      window.clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onHide);
    };
  }, [consent]);
}

/** Referral code from the link the visitor came with: kept on this device only with statistics consent
 *  (§ 25 TDDDG – it is not needed for the service), otherwise only for this page visit. */
export const REF_KEY = "kk_ref";
let refMem: string | null = null;
export function rememberRef(ref: string, kv: KV | null = localKV()) {
  refMem = ref;
  if (statisticsAllowed(kv)) { try { kv?.setItem(REF_KEY, ref); } catch { /* blocked */ } }
}
export function currentRef(kv: KV | null = localKV()): string | null { try { return kv?.getItem(REF_KEY) ?? refMem; } catch { return refMem; } }
/** Funnel step (statement created, PDF, account saved) – with the referral code, only with consent. */
export function funnel(name: string, props: Record<string, unknown> = {}) { const ref = currentRef(); trackEvent(name, { ...props, ...(ref ? { ref } : {}) }); }
